import * as Sentry from '@sentry/react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useRouteCompanyId } from '@/hooks/use-route-company-id'
import { useRouteProjectId } from '@/hooks/use-route-project-id'
import { NetworkError } from '@/services/fintalk/api/types/errors/NetworkError'
import type { Bot } from '@/services/fintalk/services/fintalk-backoffice/company-bots/list-by-company'
import { listProjectBots } from '@/services/fintalk/services/fintalk-backoffice/project-bots/list-by-project'

type GetProjectBotsLoadingState = { type: 'loading' }

type GetProjectBotsSuccessState = { type: 'success' }

type GetProjectBotsErrorState = {
  type: 'error'
  errorType: 'NETWORK_ERROR' | 'UNKNOWN_ERROR'
  eventId?: string
  isRetrying?: boolean
}

export type GetProjectBotsState =
  | GetProjectBotsLoadingState
  | GetProjectBotsSuccessState
  | GetProjectBotsErrorState

export function useGetProjectBots() {
  const companyId = useRouteCompanyId()!
  const projectId = useRouteProjectId()!

  const [bots, setBots] = useState<Bot[]>([])
  const [botsStatus, setBotsStatus] = useState<GetProjectBotsState>({
    type: 'loading',
  })

  const latestRequest = useRef(0)

  const updateBotInBots = useCallback((bot: Bot) => {
    setBots((bots) => bots.map((item) => (item._id === bot._id ? bot : item)))
  }, [])

  const loadBots = useCallback(async () => {
    const request = ++latestRequest.current

    setBots([])
    setBotsStatus((status) =>
      status.type === 'error'
        ? { ...status, isRetrying: true }
        : { type: 'loading' },
    )

    try {
      const bots = await listProjectBots(companyId, projectId)

      if (request !== latestRequest.current) return

      setBots(bots)
      setBotsStatus({ type: 'success' })
    } catch (error) {
      if (request !== latestRequest.current) return

      if (error instanceof NetworkError) {
        setBotsStatus({ type: 'error', errorType: 'NETWORK_ERROR' })
        return
      }

      const eventId = Sentry.captureException(error)

      setBotsStatus({ type: 'error', errorType: 'UNKNOWN_ERROR', eventId })
    }
  }, [companyId, projectId])

  useEffect(() => {
    loadBots()
  }, [loadBots])

  return { bots, botsStatus, loadBots, updateBotInBots }
}
